import React, { useState } from "react";
import { Form, Select, Switch, DatePicker, Tabs, Table, Input } from "antd";
import { useSettings } from "~/hooks";
import { USAGE_REPORT_QUERY } from "~/graphql/query";
import { useQuery } from "@apollo/client";
import { TimeSeriesRange } from "~/gql/graphql";
import DonutChart from "./donut-chart";
import CountLineChart from "./count-line-chart";
import PercentageLineChart from "./percentage-line-chart";
import CountBarChart from "./count-bar-chart";
import styles from "./reports-page.module.css";
import { FilterOutlined, FilterFilled } from "@ant-design/icons";
import { FilterButtonGroup } from "@design-system";
import { Spin } from "antd";
import { useTaxonomyLabel, VPDLabel } from "~/context/taxonomy-context";

const {
  ["time-range-container"]: timeRangeContainer,
  ["tab-content"]: tabContentClassName,
  ["table-container"]: tableContainerClassName,
  ["loading-overlay"]: loadingOverlayClassName,
  ["spinner-container"]: spinnerContainerClassName,
  ["report-tabs"]: reportTabsClassName,
} = styles as any;

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

type CountByField = {
  field: string;
  id: number | null;
  count: number;
};

const TimeSeriesRangeDisplayNames = {
  [TimeSeriesRange.AllTime]: "All Time",
  [TimeSeriesRange.Last12Months]: "Last 12 Months",
  [TimeSeriesRange.Last6Months]: "Last 6 Months",
  [TimeSeriesRange.Last3Months]: "Last 3 Months",
  [TimeSeriesRange.LastMonth]: "Last Month",
  [TimeSeriesRange.SinceVpd]: "Since Installing VPD",
};

export const ReportsPage = () => {
  const { settings } = useSettings();

  const taxonomy = useTaxonomyLabel();
  const PA_LABEL = taxonomy.PracticeArea;
  const FE_LABEL = taxonomy.FeeEarner;

  const [useCustomRange, setUseCustomRange] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeSeriesRange>(TimeSeriesRange.AllTime);
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);

  const { data: usageReportData, loading: usageReportLoading } = useQuery(USAGE_REPORT_QUERY, {
    variables: {
      input: {
        timeRange: useCustomRange ? undefined : timeRange,
        startEndDate: useCustomRange
          ? { startDate: startDate?.toDate(), endDate: endDate?.toDate() }
          : undefined,
      },
    },
  });

  const getPercentageFromCountByFields = (countByFields: CountByField[]) => {
    const assigned =
      countByFields.find((countByField) => countByField.field === "Assigned")?.count ?? 0;
    const unassigned =
      countByFields.find((countByField) => countByField.field === "Unassigned")?.count ?? 0;

    return assigned + unassigned > 0 ? (100 * assigned) / (assigned + unassigned) : null;
  };
  const percentageLineChartData = usageReportData?.usageReportQuery.timeSeriesData.map((data) => {
    return {
      timeLabelString: data.timeLabelString,
      percentage: getPercentageFromCountByFields(data.externalMattersStatusCounts),
    };
  });

  const handleSwitchChange = (checked: boolean) => {
    setUseCustomRange(checked);
    if (!checked) {
      setStartDate(null);
      setEndDate(null);
    }
  };

  return (
    <div style={{ maxWidth: "1600px", margin: "0 auto", padding: "0 24px" }}>
      <div
        className={timeRangeContainer}
        style={{
          background: "white",
          padding: "24px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          marginBottom: "24px",
        }}
      >
        {useCustomRange ? (
          <Form.Item label="Time Range">
            <RangePicker
              picker="month"
              style={{ width: "300px" }}
              onChange={(dates) => {
                if (dates) {
                  setStartDate(dates[0] ?? null);
                  setEndDate(dates[1] ?? null);
                }
              }}
            />
          </Form.Item>
        ) : (
          <Form.Item label="Time Range" name="timeRange">
            <Select
              onChange={(value) => setTimeRange(value)}
              style={{ width: "300px" }}
              defaultValue={TimeSeriesRange.AllTime}
            >
              {Object.keys(TimeSeriesRangeDisplayNames)
                .filter((key) => key !== "SinceVpd")
                .map((key) => {
                  const timeRangeKey = key as keyof typeof TimeSeriesRange;
                  return (
                    <Select.Option key={key} value={TimeSeriesRange[timeRangeKey]}>
                      {TimeSeriesRangeDisplayNames[timeRangeKey]}
                    </Select.Option>
                  );
                })}
            </Select>
          </Form.Item>
        )}
        <Form.Item label="Custom Range" name="customRange" style={{ marginLeft: "16px" }}>
          <Switch onChange={handleSwitchChange} />
        </Form.Item>
      </div>

      <div style={{ position: "relative" }} className={reportTabsClassName}>
        {usageReportLoading && (
          <div className={loadingOverlayClassName}>
            <div className={spinnerContainerClassName}>
              <Spin />
            </div>
          </div>
        )}
        <Tabs defaultActiveKey="status">
          <TabPane tab="Status" key="status">
            <div className={tabContentClassName}>
              <DonutChart
                data={usageReportData?.usageReportQuery.aggregateData.mattersByStatus ?? []}
                explanatoryText="This shows the current status of all VPD proposals created in the given time range."
                title="VPD Proposals by Status"
                width={400}
              />
              <DonutChart
                data={usageReportData?.usageReportQuery.aggregateData.mattersByLinkedUnlinked ?? []}
                explanatoryText={`This shows the number of VPD proposals created in the given time range that are linked to ${
                  settings?.organisationPmsName ?? "PMS"
                } matters for live cost tracking and alerts.`}
                title="VPD Proposals Linked/Unlinked"
                width={400}
              />
            </div>
          </TabPane>
          <TabPane tab="PMS matters" key="pms">
            <div className={tabContentClassName}>
              <DonutChart
                data={usageReportData?.usageReportQuery.aggregateData.externalMattersByStatus ?? []}
                title={`${settings?.organisationPmsName ?? "PMS"} Matters Linked/Unlinked`}
                explanatoryText={`This shows the total number of ${
                  settings?.organisationPmsName ?? "PMS"
                } matters that are linked to VPD proposals in the given time range. `}
                width={400}
              />
              <PercentageLineChart
                data={percentageLineChartData}
                title="Linked Percentage"
                explanatoryText={`This shows the percentage of your ${
                  settings?.organisationPmsName ?? "PMS"
                } matters that are linked to VPD proposals in the given time range.`}
                yAxisLabel="Percentage Linked"
                width={800}
              />
            </div>
          </TabPane>
          <TabPane tab={PA_LABEL} key="practiceAreas">
            <div className={tabContentClassName}>
              <DonutChart
                data={usageReportData?.usageReportQuery.aggregateData.mattersByPracticeArea ?? []}
                title={`${PA_LABEL} Proposals`}
                explanatoryText={`Total count of proposals created by each ${PA_LABEL} in the given time range.`}
                width={400}
              />
              <CountLineChart
                data={usageReportData?.usageReportQuery.timeSeriesData ?? []}
                countsArrayName="practiceAreaCounts"
                title={`Proposals by ${PA_LABEL}`}
                explanatoryText={`This shows the number of proposals created for each ${PA_LABEL} over the given time range. You can use the cumulative toggle to switch the chart to cumulative totals, and hide / show individual lines by clicking the labels below the chart.`}
                width={800}
                height={350}
              />
            </div>
          </TabPane>
          <TabPane tab={`${PA_LABEL} Breakdown`} key="practiceAreaBreakdown">
            <div className={tabContentClassName}>
              <CountBarChart
                data={usageReportData?.usageReportQuery.aggregateData.practiceAreas ?? []}
                title={`${PA_LABEL} Status Breakdown`}
                explanatoryText={`This shows the current status of each proposal by ${PA_LABEL} over the given time range.`}
                countByField="byStatus"
                width={600}
                height={500}
              />
              <CountBarChart
                data={usageReportData?.usageReportQuery.aggregateData.practiceAreas ?? []}
                title={`${PA_LABEL} Linked/Unlinked Breakdown`}
                explanatoryText={`This shows the number of ${
                  settings?.organisationPmsName ?? "PMS"
                } matters linked and unlinked to VPD proposals by ${PA_LABEL} over the given time range.`}
                countByField="byLinkedUnlinked"
                width={600}
                height={500}
              />
            </div>
          </TabPane>
          <TabPane tab={`${taxonomy.Template}s`} key="templates">
            <div className={tabContentClassName}>
              <DonutChart
                data={usageReportData?.usageReportQuery.aggregateData.templatesByPracticeArea ?? []}
                title={`${PA_LABEL} ${taxonomy.Template}s`}
                explanatoryText={`This shows the number of templates assigned to each ${PA_LABEL}.`}
                width={400}
              />
              <CountBarChart
                data={usageReportData?.usageReportQuery.aggregateData.practiceAreas ?? []}
                title={`${taxonomy.Template} Type Breakdown`}
                explanatoryText={`This shows the number of templates for each ${PA_LABEL}, broken down by ${taxonomy.Template} Type.`}
                countByField="templatesByTemplateType"
                width={600}
                height={500}
              />
            </div>
          </TabPane>
          <TabPane tab={`${FE_LABEL}s`} key="feeEarners">
            <div className={tabContentClassName}>
              <div className={tableContainerClassName}>
                <h4>
                  This table gives a breakdown of {FE_LABEL} activity over the given time range. You
                  can filter by {FE_LABEL} or {PA_LABEL}. You can also sort any column.
                </h4>
                <Table
                  dataSource={usageReportData?.usageReportQuery.aggregateData.mattersByUser ?? []}
                  columns={[
                    {
                      title: FE_LABEL,
                      dataIndex: "field",
                      sorter: (a, b) => a.field.localeCompare(b.field),
                      defaultSortOrder: "ascend",
                      width: "25%",
                      filterDropdown: ({
                        setSelectedKeys,
                        selectedKeys,
                        confirm,
                        clearFilters,
                      }) => (
                        <div style={{ padding: 8 }}>
                          <Input
                            placeholder={`Search ${FE_LABEL}`}
                            value={selectedKeys[0] as string}
                            onChange={(e) =>
                              setSelectedKeys(e.target.value ? [e.target.value] : [])
                            }
                            onPressEnter={() => confirm()}
                            style={{ width: 188, marginBottom: 8, display: "block" }}
                          />
                          <FilterButtonGroup
                            canApply={selectedKeys.length > 0}
                            canClear={selectedKeys.length > 0}
                            onClickApply={() => confirm()}
                            onClickClear={() => {
                              clearFilters && clearFilters();
                              confirm();
                            }}
                          />
                        </div>
                      ),
                      onFilter: (value, record) =>
                        record.field.toLowerCase().includes((value as string).toLowerCase()),
                      filterIcon: (filtered) =>
                        filtered ? (
                          <FilterFilled style={{ fontSize: "17px" }} />
                        ) : (
                          <FilterOutlined style={{ fontSize: "17px" }} />
                        ),
                    },
                    {
                      title: PA_LABEL,
                      dataIndex: ["metaData"],
                      render: (value, record) =>
                        record.metaData?.find((meta) => meta.key === "organisationalUnit")?.value ??
                        "N/A",
                      sorter: (a, b) => {
                        const aPA =
                          a.metaData?.find((meta) => meta.key === "organisationalUnit")?.value ??
                          "N/A";
                        const bPA =
                          b.metaData?.find((meta) => meta.key === "organisationalUnit")?.value ??
                          "N/A";
                        return aPA.localeCompare(bPA);
                      },
                      width: "25%",
                      filterDropdown: ({
                        setSelectedKeys,
                        selectedKeys,
                        confirm,
                        clearFilters,
                      }) => (
                        <div style={{ padding: 8 }}>
                          <Input
                            placeholder={`Search ${PA_LABEL}`}
                            value={selectedKeys[0] as string}
                            onChange={(e) =>
                              setSelectedKeys(e.target.value ? [e.target.value] : [])
                            }
                            onPressEnter={() => confirm()}
                            style={{ width: 188, marginBottom: 8, display: "block" }}
                          />
                          <FilterButtonGroup
                            canApply={selectedKeys.length > 0}
                            canClear={selectedKeys.length > 0}
                            onClickApply={() => confirm()}
                            onClickClear={() => {
                              clearFilters && clearFilters();
                              confirm();
                            }}
                          />
                        </div>
                      ),
                      onFilter: (value, record) => {
                        const practiceArea =
                          record.metaData?.find((meta) => meta.key === "organisationalUnit")
                            ?.value ?? "N/A";
                        return practiceArea.toLowerCase().includes((value as string).toLowerCase());
                      },
                      filterIcon: (filtered) =>
                        filtered ? (
                          <FilterFilled style={{ fontSize: "17px" }} />
                        ) : (
                          <FilterOutlined style={{ fontSize: "17px" }} />
                        ),
                    },
                    {
                      title: "Matters Created in VPD in Selected Time Range",
                      dataIndex: "count",
                      sorter: (a, b) => a.count - b.count,
                      width: "25%",
                    },
                    {
                      title: "Last Login",
                      dataIndex: ["metaData"],
                      render: (value, record) => {
                        const lastLoginAt = record.metaData?.find(
                          (meta) => meta.key === "lastLoginAt"
                        )?.value;
                        if (lastLoginAt && lastLoginAt !== "Unknown") {
                          const date = new Date(lastLoginAt);
                          return date.toLocaleString("en-GB");
                        }
                        return "N/A";
                      },
                      sorter: (a, b) => {
                        const aLogin = a.metaData?.find(
                          (meta) => meta.key === "lastLoginAt"
                        )?.value;
                        const bLogin = b.metaData?.find(
                          (meta) => meta.key === "lastLoginAt"
                        )?.value;
                        if (aLogin === "Unknown" || !aLogin) return 1;
                        if (bLogin === "Unknown" || !bLogin) return -1;
                        return new Date(aLogin).getTime() - new Date(bLogin).getTime();
                      },
                      width: "25%",
                    },
                  ]}
                  pagination={{ pageSize: 6 }}
                  sortDirections={["ascend", "descend", "ascend"]}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};
